import { makeStyles } from '@mui/styles';
import { styled, createTheme } from '@mui/material/styles';

/**
 * Utility functions for MUI v5 styling
 * This file provides a consistent way to handle styling across the project
 * while maintaining backward compatibility with MUI v4 patterns
 */

// Default theme configuration
const defaultTheme = createTheme({
  spacing: (factor) => `${8 * factor}px`
});

// Custom hook that combines makeStyles with theme
export const useCustomStyles = (stylesCallback) => {
  const useStyles = makeStyles((theme = defaultTheme) => stylesCallback(theme), {
    defaultTheme
  });
  return useStyles();
};

// For components that need styled-components like syntax with MUI
export const muiStyled = styled;

// For components that still need makeStyles (will help with future migration)
export const createStyles = (styles, options) => {
  console.warn(
    'Warning: makeStyles is deprecated in MUI v5. Consider migrating to styled-components or @mui/material/styles.'
  );
  return makeStyles(styles, { defaultTheme, ...options });
};

// Re-export makeStyles with default theme
export const makeStylesWithTheme = (styles, options = {}) => 
  makeStyles(styles, { defaultTheme, ...options });

// Helper for converting withStyles to styled API
export const createStyledMuiComponent = (Component, styles) => {
  const StyledComponent = styled(Component)(({ theme = defaultTheme }) => {
    if (typeof styles === 'function') {
      return styles(theme);
    }
    return styles;
  });
  return StyledComponent;
};

// Helper for theme-aware components
export const createThemedComponent = (Component, styles) => {
  return styled(Component)(({ theme = defaultTheme }) => ({
    ...styles,
    ...(typeof styles === 'function' ? styles(theme) : {}),
  }));
};

// Helper for creating styled MUI components with props
export const createStyledMuiComponentWithProps = (Component, styles) => {
  return styled(Component)(({ theme = defaultTheme, ...props }) => ({
    ...(typeof styles === 'function' ? styles(theme, props) : styles),
  }));
};

// Helper for creating styled components with dynamic props
export const createDynamicStyledComponent = (Component, stylesFn) => {
  return styled(Component)(({ theme = defaultTheme, ...props }) => stylesFn(theme, props));
};

// Helper for creating styled components with variants
export const createVariantComponent = (Component, variants, defaultVariant = 'default') => {
  return styled(Component)(({ theme = defaultTheme, variant = defaultVariant }) => ({
    ...(variants[variant] || variants[defaultVariant]),
  }));
};

// Re-export original makeStyles for compatibility
export { makeStyles };